import React, { useEffect, useRef, useState } from "react";

/** "Trash-Bot Rises" — catch recyclables as Earth Jr. to defeat the Trash-Bot. */

const W = 400;
const H = 560;
const CATCH_Y = H - 70;

const GOOD = ["🔋", "💻", "📱", "👕", "🥫", "📺", "♻️", "👟", "🧴", "📦"];
const BAD = ["🛢️", "💣", "⚙️", "🗑️", "💨"];

type Item = { x: number; y: number; vy: number; emoji: string; bad: boolean };
type Float = { x: number; y: number; text: string; life: number; color: string };

type Phase = "ready" | "playing" | "won" | "lost";

const TrashBotGame: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [phase, setPhase] = useState<Phase>("ready");
  const [finalScore, setFinalScore] = useState(0);

  const g = useRef({
    running: false,
    catcherX: W / 2,
    targetX: W / 2,
    items: [] as Item[],
    floats: [] as Float[],
    bossHP: 100,
    hearts: 3,
    score: 0,
    combo: 0,
    spawnTimer: 0,
    spawnEvery: 55,
    elapsed: 0,
    shake: 0,
    flash: 0,
  });

  const start = () => {
    const s = g.current;
    s.catcherX = W / 2; s.targetX = W / 2;
    s.items = []; s.floats = [];
    s.bossHP = 100; s.hearts = 3; s.score = 0; s.combo = 0;
    s.spawnTimer = 0; s.spawnEvery = 55; s.elapsed = 0; s.shake = 0; s.flash = 0;
    s.running = true;
    setPhase("playing");
  };

  // pointer / keyboard control
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const toLogicalX = (clientX: number) => {
      const r = canvas.getBoundingClientRect();
      return ((clientX - r.left) / r.width) * W;
    };
    const move = (e: PointerEvent) => { g.current.targetX = Math.max(0, Math.min(W, toLogicalX(e.clientX))); };
    const key = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") g.current.targetX = Math.max(0, g.current.targetX - 40);
      if (e.key === "ArrowRight") g.current.targetX = Math.min(W, g.current.targetX + 40);
    };
    canvas.addEventListener("pointermove", move);
    canvas.addEventListener("pointerdown", move);
    window.addEventListener("keydown", key);
    return () => {
      canvas.removeEventListener("pointermove", move);
      canvas.removeEventListener("pointerdown", move);
      window.removeEventListener("keydown", key);
    };
  }, []);

  // game loop
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const game = g.current;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);
    let raf = 0;

    const loop = () => {
      const s = g.current;
      if (!s.running) return;
      s.elapsed++;

      // difficulty ramp
      if (s.spawnEvery > 26 && s.elapsed % 240 === 0) s.spawnEvery -= 3;

      // spawn
      s.spawnTimer++;
      if (s.spawnTimer >= s.spawnEvery) {
        s.spawnTimer = 0;
        const bad = Math.random() < 0.3;
        s.items.push({
          x: 32 + Math.random() * (W - 64),
          y: -20,
          vy: 2.0 + Math.random() * 1.2 + s.elapsed / 2600,
          emoji: bad ? BAD[(Math.random() * BAD.length) | 0] : GOOD[(Math.random() * GOOD.length) | 0],
          bad,
        });
      }

      // catcher follow
      s.catcherX += (s.targetX - s.catcherX) * 0.25;

      // update items
      for (let i = s.items.length - 1; i >= 0; i--) {
        const it = s.items[i];
        it.y += it.vy;
        const caught = it.y >= CATCH_Y - 26 && it.y <= CATCH_Y + 30 && Math.abs(it.x - s.catcherX) < 42;
        if (caught) {
          if (it.bad) {
            s.hearts -= 1; s.combo = 0; s.flash = 12; s.shake = 10;
            s.floats.push({ x: it.x, y: it.y, text: "OUCH!", life: 40, color: "#e23b3b" });
          } else {
            s.combo += 1;
            const pts = 10 + Math.min(s.combo, 8);
            s.score += pts; s.bossHP = Math.max(0, s.bossHP - 5); s.shake = 5;
            s.floats.push({ x: it.x, y: it.y, text: "+" + pts, life: 40, color: "#17a34a" });
          }
          s.items.splice(i, 1);
          continue;
        }
        if (it.y > H + 24) {
          if (!it.bad) { // missed a recyclable → Trash-Bot grows
            s.bossHP = Math.min(100, s.bossHP + 3); s.combo = 0;
            s.floats.push({ x: it.x, y: H - 90, text: "+wasted", life: 36, color: "#b06a17" });
          }
          s.items.splice(i, 1);
        }
      }

      // floats
      for (let i = s.floats.length - 1; i >= 0; i--) {
        const f = s.floats[i]; f.y -= 0.8; f.life--;
        if (f.life <= 0) s.floats.splice(i, 1);
      }

      if (s.shake > 0) s.shake -= 1;
      if (s.flash > 0) s.flash -= 1;

      // win / lose
      if (s.bossHP <= 0) { s.running = false; setFinalScore(s.score); setPhase("won"); }
      else if (s.hearts <= 0) { s.running = false; setFinalScore(s.score); setPhase("lost"); }

      draw(ctx, s);
      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); game.running = false; };
  }, [phase]);

  return (
    <div className="tbg-wrap">
      <div className="tbg-stage">
        <canvas ref={canvasRef} className="tbg-canvas" style={{ aspectRatio: `${W} / ${H}` }} />

        {phase === "ready" && (
          <div className="tbg-overlay">
            <div className="tbg-kicker">★ Eco-Allies Arcade ★</div>
            <h3 className="tbg-title">TRASH-BOT<br />RISES!</h3>
            <p className="tbg-sub">Play as <strong>Earth Jr.</strong> 🌎 — catch the recyclables to shrink the Trash-Bot, and dodge its junk!</p>
            <button className="tbg-btn" onClick={start}>▶ PLAY</button>
            <p className="tbg-hint">Move with your mouse or finger</p>
          </div>
        )}
        {phase === "won" && (
          <div className="tbg-overlay tbg-win">
            <div className="tbg-burst">🌎💥</div>
            <h3 className="tbg-title">YOU SAVED<br />MOTHER EARTH!</h3>
            <p className="tbg-sub">Score: <strong>{finalScore}</strong> · You're a true Eco-Hero! 🦸</p>
            <button className="tbg-btn" onClick={start}>↻ PLAY AGAIN</button>
          </div>
        )}
        {phase === "lost" && (
          <div className="tbg-overlay tbg-lose">
            <div className="tbg-burst">🤖</div>
            <h3 className="tbg-title">TRASH-BOT<br />GREW TOO BIG…</h3>
            <p className="tbg-sub">Score: <strong>{finalScore}</strong> · The city needs you — try again!</p>
            <button className="tbg-btn" onClick={start}>↻ TRY AGAIN</button>
          </div>
        )}
      </div>
    </div>
  );
};

function draw(ctx: CanvasRenderingContext2D, s: any) {
  const ox = s.shake ? (Math.random() - 0.5) * s.shake : 0;
  const oy = s.shake ? (Math.random() - 0.5) * s.shake : 0;
  ctx.save();
  ctx.clearRect(0, 0, W, H);
  ctx.translate(ox, oy);

  // sky
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, "#bfe8ff");
  grad.addColorStop(1, "#eafbf0");
  ctx.fillStyle = grad;
  ctx.fillRect(-10, -10, W + 20, H + 20);
  // halftone dots
  ctx.fillStyle = "rgba(255,255,255,.5)";
  for (let y = 10; y < H; y += 26) for (let x = 10; x < W; x += 26) { ctx.beginPath(); ctx.arc(x, y, 2, 0, 7); ctx.fill(); }

  // Trash-Bot (boss)
  const bossY = 70 + (100 - s.bossHP) * 0.1;
  const bossSize = 64 + s.bossHP * 0.5; // shrinks as HP drops
  ctx.font = `${bossSize}px serif`;
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText("🤖", W / 2 + (s.shake ? (Math.random() - .5) * 4 : 0), bossY);

  // boss HP bar
  ctx.fillStyle = "#0b1f14"; roundRect(ctx, W / 2 - 90, 16, 180, 16, 8); ctx.fill();
  ctx.fillStyle = "#e23b3b"; roundRect(ctx, W / 2 - 87, 19, 174 * (s.bossHP / 100), 10, 5); ctx.fill();
  ctx.fillStyle = "#fff"; ctx.font = "700 11px Inter, sans-serif";
  ctx.fillText("TRASH-BOT", W / 2, 24);

  // items with colored glow
  for (const it of s.items) {
    ctx.beginPath();
    ctx.fillStyle = it.bad ? "rgba(226,59,59,.28)" : "rgba(23,163,74,.28)";
    ctx.arc(it.x, it.y, 22, 0, 7); ctx.fill();
    ctx.font = "30px serif";
    ctx.fillText(it.emoji, it.x, it.y);
  }

  // Earth Jr. catcher with cape
  ctx.fillStyle = "#17a34a";
  ctx.beginPath();
  ctx.moveTo(s.catcherX - 26, CATCH_Y + 4);
  ctx.lineTo(s.catcherX + 26, CATCH_Y + 4);
  ctx.lineTo(s.catcherX, CATCH_Y + 34);
  ctx.closePath(); ctx.fill();
  ctx.font = "46px serif";
  ctx.fillText("🌎", s.catcherX, CATCH_Y);

  // floats
  ctx.textAlign = "center";
  for (const f of s.floats) {
    ctx.globalAlpha = Math.max(0, f.life / 40);
    ctx.fillStyle = f.color;
    ctx.font = "800 18px 'Bangers', Inter, sans-serif";
    ctx.fillText(f.text, f.x, f.y);
    ctx.globalAlpha = 1;
  }

  // HUD
  ctx.textAlign = "left";
  ctx.fillStyle = "#0b1f14";
  ctx.font = "800 20px 'Bangers', Inter, sans-serif";
  ctx.fillText("SCORE " + s.score, 14, H - 18);
  ctx.textAlign = "right";
  ctx.font = "20px serif";
  ctx.fillText("❤️".repeat(Math.max(0, s.hearts)), W - 12, H - 16);

  // red flash on hit
  if (s.flash > 0) {
    ctx.fillStyle = `rgba(226,59,59,${0.04 * s.flash})`;
    ctx.fillRect(-10, -10, W + 20, H + 20);
  }
  ctx.restore();
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export default TrashBotGame;
